import { Plus, X } from "lucide-react";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import { ErrorState } from "@/components/shared/error-state";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { EntityCombobox } from "@/components/ui/entity-combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { usePersonRoles, useSetPersonRoles } from "@/hooks/use-persons";
import { useRoleLevelOptions } from "@/hooks/use-role-level-applicability";
import { todayLocal } from "@/lib/dates";
import { enforceSinglePrimary, isPreviousAssignment } from "@/lib/person-roles";

interface PersonRolesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  personId: string;
  personName: string;
}

interface Assignment {
  // Stable client-side row id — roleId is empty until a role is picked, so it
  // can't be the React key or the identity used by the per-row mutators.
  key: string;
  roleId: string;
  isPrimary: boolean;
  roleLevelId: string | null;
  startDate: string | null;
  endDate: string | null;
}

/** Sentinel for "no level" — Radix Select forbids an empty-string item value. */
const NO_LEVEL = "__none__";

/**
 * The level picker for one assignment row, scoped to the levels the chosen role
 * opts into (WI-104). Its own component so each row can run its own query for
 * its own role.
 *
 * Renders nothing at all when the role uses no levels — an empty picker would
 * imply the admin forgot something rather than that the role has no ladder.
 * Levels are a small per-role set, so this is a plain Select, not the
 * server-searchable EntityCombobox (see the frontend rule on entity selects).
 */
function RoleLevelSelect({
  roleId,
  value,
  onChange,
}: {
  roleId: string;
  value: string | null;
  onChange: (roleLevelId: string | null) => void;
}) {
  const { data: options = [], isLoading } = useRoleLevelOptions(roleId, roleId !== "");
  // Radix's trigger is a button, not a native control, so a wrapping <label>
  // wouldn't associate — the caption is linked with aria-labelledby instead.
  const labelId = useId();
  // Only levels this role opts into, and never a retired one — matching what
  // the server's assertRoleLevelsApplicable will accept.
  const selectable = options.filter((o) => o.applies && !o.retired);

  if (roleId === "" || isLoading || selectable.length === 0) return null;

  return (
    <div className="flex flex-col gap-1">
      <span id={labelId} className="text-xs text-muted-foreground">
        Level
      </span>
      <Select value={value ?? NO_LEVEL} onValueChange={(v) => onChange(v === NO_LEVEL ? null : v)}>
        <SelectTrigger aria-labelledby={labelId} className="h-8 text-sm">
          <SelectValue placeholder="No level" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NO_LEVEL}>No level</SelectItem>
          {selectable.map((o) => (
            <SelectItem key={o.id} value={o.id}>
              {o.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

/**
 * Admin dialog for managing the roles a person is assigned to. Each role is a
 * record added via the "Add role" button and picked with a single-select
 * combobox; the record carries start/end dates and (for current roles) the
 * single-primary flag. Current and previous roles live on separate tabs — a
 * role moves to Previous once it's been saved with an end date in the past.
 * Submits the full desired-set to PUT /api/persons/:id/roles, which atomically
 * replaces it.
 */

export function PersonRolesDialog({
  open,
  onOpenChange,
  personId,
  personName,
}: PersonRolesDialogProps) {
  const {
    data: existing,
    isLoading: loadingExisting,
    isError: existingFailed,
    refetch: refetchExisting,
  } = usePersonRoles(personId);
  const setRoles = useSetPersonRoles(personId);

  /**
   * Saving replaces the person's WHOLE role set (`applyPersonRolesTx` deletes
   * then re-inserts), so the draft must never be treated as authoritative until
   * the saved roles have actually arrived. A failed fetch leaves `existing`
   * undefined with `isLoading` false, which read as "this person has no roles"
   * and let Save wipe every assignment they held.
   */
  const settled = !loadingExisting && !existingFailed && existing !== undefined;

  const [draft, setDraft] = useState<Assignment[]>([]);
  const [roleTab, setRoleTab] = useState<"current" | "previous">("current");
  // Role changes are effective-dated like every other person field: today/past
  // applies now, a future date schedules a pending change (WI-055).
  const [effectiveDate, setEffectiveDate] = useState(todayLocal());

  // Seed the draft from the saved roles ONCE per open-session — the first time
  // `existing` is available after the dialog opens. A background refetch (e.g.
  // window refocus while the dialog is open) gives `existing` a new reference;
  // without this guard the effect would re-fire and wipe the admin's unsaved
  // edits. The ref resets on close so the next open re-seeds.
  const seededForOpen = useRef(false);
  useEffect(() => {
    if (!open) {
      seededForOpen.current = false;
      return;
    }
    if (seededForOpen.current || !existing) return;
    setDraft(
      enforceSinglePrimary(
        existing.map((e) => ({
          key: e.roleId,
          roleId: e.roleId,
          isPrimary: e.isPrimary,
          roleLevelId: e.roleLevelId,
          startDate: e.startDate,
          endDate: e.endDate,
        })),
        todayLocal(),
      ),
    );
    setRoleTab("current");
    seededForOpen.current = true;
  }, [open, existing]);

  function addRole() {
    setDraft((prev) => [
      ...prev,
      {
        key: crypto.randomUUID(),
        roleId: "",
        isPrimary: false,
        roleLevelId: null,
        startDate: todayLocal(),
        endDate: null,
      },
    ]);
  }

  function removeRow(key: string) {
    setDraft((prev) => prev.filter((d) => d.key !== key));
  }

  function setRole(key: string, roleId: string | null) {
    setDraft((prev) =>
      prev.map((d) => {
        if (d.key !== key) return d;
        // Levels belong to a role, so a held level can't survive a role swap —
        // the server rejects a level the new role doesn't opt into.
        const next = roleId ?? "";
        return { ...d, roleId: next, roleLevelId: next === d.roleId ? d.roleLevelId : null };
      }),
    );
  }

  function setLevel(key: string, roleLevelId: string | null) {
    setDraft((prev) => prev.map((d) => (d.key === key ? { ...d, roleLevelId } : d)));
  }

  function setPrimary(key: string) {
    // At most one primary; toggle the target and clear the rest.
    setDraft((prev) => prev.map((d) => ({ ...d, isPrimary: d.key === key })));
  }

  function setDate(key: string, field: "startDate" | "endDate", value: string) {
    setDraft((prev) => prev.map((d) => (d.key === key ? { ...d, [field]: value || null } : d)));
  }

  // Invalid date combination: both present and the end precedes the start.
  function rowInvalid(d: Assignment): boolean {
    return Boolean(d.startDate && d.endDate && d.endDate < d.startDate);
  }
  // A role chosen on more than one record (the DB rejects duplicate pairs).
  const dupeRoleIds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const d of draft) {
      if (d.roleId) counts.set(d.roleId, (counts.get(d.roleId) ?? 0) + 1);
    }
    return new Set([...counts.entries()].filter(([, n]) => n > 1).map(([id]) => id));
  }, [draft]);

  const hasInvalidDates = draft.some(rowInvalid);
  const hasIncomplete = draft.some((d) => !d.roleId);
  const hasDuplicate = dupeRoleIds.size > 0;
  const canSave = settled && !hasInvalidDates && !hasIncomplete && !hasDuplicate;

  // Tab membership is fixed by each row's SAVED identity (its stable `key`), not
  // its currently-picked `roleId` — so picking a previously-held role in a NEW
  // row doesn't drag that row into Previous before it's even saved. Seeded rows
  // use `key = roleId`, so this is the set of keys for saved assignments whose
  // end date has already passed; a freshly-added row carries a random key and is
  // therefore always Current until saved.
  const previousKeys = useMemo(() => {
    const today = todayLocal();
    return new Set(
      (existing ?? []).filter((e) => isPreviousAssignment(e, today)).map((e) => e.roleId),
    );
  }, [existing]);
  // Default the effective date to today on each open (the dialog isn't
  // remounted between opens, so a prior future pick would otherwise linger).
  useEffect(() => {
    if (open) setEffectiveDate(todayLocal());
  }, [open]);

  const isPreviousRow = (d: Assignment) => previousKeys.has(d.key);
  const currentCount = draft.filter((d) => !isPreviousRow(d)).length;
  const previousCount = draft.length - currentCount;
  const shown = draft.filter((d) =>
    roleTab === "previous" ? isPreviousRow(d) : !isPreviousRow(d),
  );

  async function save() {
    if (!canSave || !effectiveDate) return;
    try {
      await setRoles.mutateAsync({
        effectiveDate,
        assignments: draft.map((d) => ({
          roleId: d.roleId,
          isPrimary: d.isPrimary,
          roleLevelId: d.roleLevelId,
          startDate: d.startDate,
          endDate: d.endDate,
        })),
      });
      onOpenChange(false);
    } catch {
      // useSetPersonRoles surfaces the error via a toast; keep the dialog open
      // so the admin can correct the dates rather than losing their edits.
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Manage roles</DialogTitle>
          <DialogDescription>
            Add the roles {personName} holds. The primary role is the one displayed first. Give a
            role an end date in the past and save to move it to the Previous tab; leave the end date
            blank for an ongoing role.
          </DialogDescription>
        </DialogHeader>
        {loadingExisting ? (
          <div className="space-y-2">
            {["a", "b", "c"].map((k) => (
              <Skeleton key={k} className="h-10 w-full" />
            ))}
          </div>
        ) : existingFailed ? (
          <ErrorState
            className="py-4"
            message="Couldn't load this person's roles"
            onRetry={() => {
              void refetchExisting();
            }}
          />
        ) : (
          <div className="space-y-4">
            <div className="flex gap-1 border-b">
              {(["current", "previous"] as const).map((tab) => (
                <button
                  key={tab}
                  type="button"
                  onClick={() => setRoleTab(tab)}
                  className={`-mb-px border-b-2 px-3 py-1.5 text-sm font-medium transition-colors ${
                    roleTab === tab
                      ? "border-primary text-foreground"
                      : "border-transparent text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {tab === "current" ? "Current" : "Previous"} (
                  {tab === "current" ? currentCount : previousCount})
                </button>
              ))}
            </div>
            {shown.length > 0 ? (
              <ul className="space-y-1">
                {shown.map((d) => (
                  <li key={d.key} className="space-y-2 rounded-md border bg-card px-3 py-2">
                    <div className="flex items-center gap-2">
                      <div className="flex-1">
                        <EntityCombobox
                          entity="roles"
                          value={d.roleId || null}
                          onChange={(id) => setRole(d.key, id)}
                          filters={{ status: "active" }}
                          placeholder="Select a role…"
                          searchPlaceholder="Search roles…"
                        />
                      </div>
                      {roleTab === "current" && (
                        <label className="flex shrink-0 items-center gap-1 text-xs">
                          <input
                            type="radio"
                            name="primary-role"
                            checked={d.isPrimary}
                            disabled={!d.roleId}
                            onChange={() => setPrimary(d.key)}
                          />
                          Primary
                        </label>
                      )}
                      <button
                        type="button"
                        onClick={() => removeRow(d.key)}
                        aria-label="Remove role"
                        className="shrink-0 rounded p-1 text-muted-foreground transition-colors hover:text-destructive"
                      >
                        <X className="size-4" />
                      </button>
                    </div>
                    <RoleLevelSelect
                      roleId={d.roleId}
                      value={d.roleLevelId}
                      onChange={(id) => setLevel(d.key, id)}
                    />
                    <div className="flex gap-3">
                      <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                        Start date
                        <input
                          type="date"
                          value={d.startDate ?? ""}
                          onChange={(e) => setDate(d.key, "startDate", e.target.value)}
                          className="rounded-md border bg-background px-2 py-1 text-sm text-foreground"
                        />
                      </label>
                      <label className="flex flex-1 flex-col gap-1 text-xs text-muted-foreground">
                        End date
                        <input
                          type="date"
                          value={d.endDate ?? ""}
                          onChange={(e) => setDate(d.key, "endDate", e.target.value)}
                          className="rounded-md border bg-background px-2 py-1 text-sm text-foreground"
                        />
                      </label>
                    </div>
                    {rowInvalid(d) && (
                      <p className="text-xs text-destructive">
                        End date can’t be before the start date.
                      </p>
                    )}
                    {d.roleId && dupeRoleIds.has(d.roleId) && (
                      <p className="text-xs text-destructive">This role is already added.</p>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">
                {roleTab === "current" ? "No current roles yet." : "No previous roles."}
              </p>
            )}
            {roleTab === "current" && (
              <Button type="button" variant="outline" size="sm" onClick={addRole}>
                <Plus className="mr-1 size-4" />
                Add role
              </Button>
            )}
          </div>
        )}
        <div className="flex flex-col gap-1 border-t pt-3">
          <label htmlFor="roles-effective-date" className="text-xs font-medium">
            Effective date
          </label>
          <input
            id="roles-effective-date"
            type="date"
            value={effectiveDate}
            onChange={(e) => setEffectiveDate(e.target.value)}
            className="w-44 rounded-md border bg-background px-2 py-1 text-sm text-foreground"
          />
          <p className="text-xs text-muted-foreground">
            {effectiveDate && effectiveDate > todayLocal()
              ? "Scheduled — the role change applies on this date."
              : "Applies immediately."}
          </p>
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={save} disabled={setRoles.isPending || !canSave || !effectiveDate}>
            {setRoles.isPending ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
